import React from 'react';
import styled from 'styled-components';
import { Check, X } from 'lucide-react';

interface PasswordRequirement {
  text: string;
  isValid: boolean;
}

interface PasswordRequirementsProps {
  password: string;
  show: boolean;
}

const RequirementsContainer = styled.div<{ show: boolean }>`
  max-height: ${({ show }) => (show ? '200px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ show, theme }) => (show ? theme.spacing.sm : '0')};
  margin-top: ${({ show, theme }) => (show ? theme.spacing.xs : '0')};
`;

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`;

const RequirementItem = styled.li<{ isValid: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme, isValid }) => (isValid ? theme.colors.success : theme.colors.textSecondary)};
`;

const IconWrapper = styled.span<{ isValid: boolean }>`
  display: flex;
  align-items: center;
  color: ${({ theme, isValid }) => (isValid ? theme.colors.success : theme.colors.textSecondary)};
`;

const checkPasswordRequirements = (password: string): PasswordRequirement[] => {
  const requirements: PasswordRequirement[] = [
    {
      text: 'At least 8 characters long (12+ recommended)',
      isValid: password.length >= 8,
    },
    {
      text: 'Contains uppercase letter (A-Z)',
      isValid: /[A-Z]/.test(password),
    },
    {
      text: 'Contains lowercase letter (a-z)',
      isValid: /[a-z]/.test(password),
    },
    {
      text: 'Contains a number (0-9)',
      isValid: /[0-9]/.test(password),
    },
    {
      text: 'Contains special character (!@#$%^&*)',
      // eslint-disable-next-line no-useless-escape
      isValid: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
    },
  ];

  // Add pattern warnings
  if (password.length > 0) {
    if (/(.)\1{2,}/.test(password)) {
      requirements.push({
        text: 'Avoid repeating characters',
        isValid: false,
      });
    }

    if (/123|abc|qwe|password|admin|user/i.test(password)) {
      requirements.push({
        text: 'Avoid common patterns and dictionary words',
        isValid: false,
      });
    }
  }

  return requirements;
};

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ password, show }) => {
  const requirements = checkPasswordRequirements(password);

  return (
    <RequirementsContainer show={show}>
      <RequirementsList>
        {requirements.map((requirement, index) => (
          <RequirementItem key={index} isValid={requirement.isValid}>
            <IconWrapper isValid={requirement.isValid}>
              {requirement.isValid ? <Check size={16} /> : <X size={16} />}
            </IconWrapper>
            {requirement.text}
          </RequirementItem>
        ))}
      </RequirementsList>
    </RequirementsContainer>
  );
};

export default PasswordRequirements;
